/**
 * useSessionHistory — local session history + derived stats.
 *
 * Persists the last 100 completed sessions to localStorage and derives:
 *   - totalSessions, totalMinutes
 *   - mostUsedPreset
 *   - currentStreak, longestStreak (consecutive-day calculation)
 *
 * Also fires a "Achievement Unlocked" toast when the cumulative count of
 * unlocked achievements increases. Achievement definitions (`ACHIEVEMENTS`,
 * `computeProgress`) are passed in by the caller to avoid coupling this hook
 * to a specific achievements UI module.
 *
 * Usage:
 *   const history = useSessionHistory({
 *     ACHIEVEMENTS, computeProgress,
 *     toast,
 *   });
 *   history.saveSession({ preset, duration, beatType, baseFrequency, completed });
 */
import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'neurowave_session_history';
const MAX_ENTRIES = 100;

const _emptyStats = () => ({
  totalSessions: 0,
  totalMinutes: 0,
  mostUsedPreset: null,
  currentStreak: 0,
  longestStreak: 0,
});

/**
 * Pure stat derivation — no React, easy to unit-test.
 * @param {Array} history
 * @returns stats object
 */
export function deriveStats(history) {
  if (!history || history.length === 0) return _emptyStats();

  const totalSessions = history.length;
  const totalMinutes = history.reduce(
    (sum, s) => sum + Math.round((s.duration || 0) / 60),
    0,
  );

  const presetCounts = {};
  history.forEach((s) => {
    presetCounts[s.preset] = (presetCounts[s.preset] || 0) + 1;
  });
  const mostUsedPreset = Object.keys(presetCounts).reduce(
    (a, b) => (presetCounts[a] > presetCounts[b] ? a : b),
    null,
  );

  // Consecutive-day streaks. Sort newest-first, walk backward.
  const sorted = [...history].sort((a, b) => new Date(b.date) - new Date(a.date));
  let currentStreak = 0;
  let longestStreak = 0;
  let tempStreak = 0;
  let lastDate = null;
  sorted.forEach((s) => {
    const sessionDate = new Date(s.date).toDateString();
    if (!lastDate) {
      tempStreak = 1;
      currentStreak = 1;
    } else {
      const dayDiff = Math.floor(
        (new Date(lastDate) - new Date(sessionDate)) / (1000 * 60 * 60 * 24),
      );
      if (dayDiff === 1) {
        tempStreak += 1;
        if (lastDate === new Date().toDateString() || dayDiff === 1) {
          currentStreak = tempStreak;
        }
      } else if (dayDiff > 1) {
        tempStreak = 1;
      }
    }
    longestStreak = Math.max(longestStreak, tempStreak);
    lastDate = sessionDate;
  });

  return { totalSessions, totalMinutes, mostUsedPreset, currentStreak, longestStreak };
}

export function useSessionHistory({ ACHIEVEMENTS, computeProgress, toast }) {
  const [sessionHistory, setSessionHistory] = useState([]);
  const [totalStats, setTotalStats] = useState(_emptyStats());
  const [lastUnlockedCount, setLastUnlockedCount] = useState(0);

  // Hydrate from localStorage once on mount.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const history = JSON.parse(raw);
      setSessionHistory(history);
      setTotalStats(deriveStats(history));
      if (ACHIEVEMENTS && computeProgress) {
        const progress = computeProgress(history);
        const unlockedCount = ACHIEVEMENTS.filter(
          (a) => (progress[a.type] || 0) >= a.target,
        ).length;
        setLastUnlockedCount(unlockedCount);
      }
    } catch (e) {
      console.error('Error loading session history:', e);
    }
    // Mount-only hydrate; we deliberately don't re-hydrate when the
    // ACHIEVEMENTS module reference identity changes.
  }, []);

  /**
   * Persist a finished session. Pushes to the front, caps the array, refreshes
   * stats, and fires the "Achievement Unlocked" toast if a new badge cleared
   * its threshold.
   */
  const saveSession = useCallback((sessionData) => {
    try {
      const session = {
        id: Date.now(),
        date: new Date().toISOString(),
        preset: sessionData.preset || 'Custom',
        duration: sessionData.duration || 0,
        beatType: sessionData.beatType,
        baseFrequency: sessionData.baseFrequency,
        completed: !!sessionData.completed,
      };

      const updated = [session, ...sessionHistory].slice(0, MAX_ENTRIES);
      setSessionHistory(updated);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(updated)); }
      catch (e) { console.error('Persist session-history failed:', e); }

      const newStats = deriveStats(updated);
      setTotalStats(newStats);

      if (ACHIEVEMENTS && computeProgress) {
        const progress = computeProgress(updated);
        const newUnlocked = ACHIEVEMENTS.filter(
          (a) => (progress[a.type] || 0) >= a.target,
        );
        if (newUnlocked.length > lastUnlockedCount) {
          const latest = newUnlocked[newUnlocked.length - 1];
          setLastUnlockedCount(newUnlocked.length);
          if (toast && latest) {
            toast({
              title: `Achievement Unlocked: ${latest.name}`,
              description: latest.desc,
            });
          }
        }
      }

      console.log('✅ Session saved to history:', session);
    } catch (e) {
      console.error('Error saving session to history:', e);
    }
  }, [sessionHistory, lastUnlockedCount, ACHIEVEMENTS, computeProgress, toast]);

  return {
    sessionHistory,
    totalStats,
    lastUnlockedCount,
    saveSession,
  };
}

export default useSessionHistory;
